import {
  chakra,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { RouterOutputs } from '@duke-hq/svc-api/admin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Reorder, useDragControls } from 'framer-motion';
import {
  BanIcon,
  GripVerticalIcon,
  Layers,
  MoreHorizontalIcon,
  PencilIcon,
  Replace,
  Trash2Icon,
} from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { CourseItemMarkSupersededModal } from '../CourseItemMarkSupersededModal/CourseItemMarkSupersededModal';
import { CourseItemStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseItemStatus.schema';
import {
  UnitCoverageBadge,
  type ResourceCoverageLike,
} from '../../components/translations/TranslationCoverageBadge';

export type CourseItemType = NonNullable<RouterOutputs['courses']['byId']>['courseItems'][number];

const ListItem = chakra(Reorder.Item);

export function Item({
  item,
  courseId,
  allCourseItems,
  scheduledUnitIds,
  setSelectedCourseItemId,
  setIsDeleteConfirmationAlertOpen,
  saveReorder,
  coverage,
  coverageLanguages,
}: {
  item: CourseItemType;
  courseId: string;
  allCourseItems: CourseItemType[];
  scheduledUnitIds: Set<string>;
  setSelectedCourseItemId: (_resourceId: string) => void;
  setIsDeleteConfirmationAlertOpen: (_isOpen: boolean) => void;
  saveReorder: () => void;
  coverage?: ResourceCoverageLike[];
  coverageLanguages?: readonly string[];
}) {
  const navigate = useNavigate();
  const dragControls = useDragControls();
  const {
    isOpen: isSupersededModalOpen,
    onOpen: onSupersededModalOpen,
    onClose: onSupersededModalClose,
  } = useDisclosure();

  const handleEditUnit = useCallback(() => {
    if (item.unitOfCompetency !== null) {
      navigate(`/units/${item.unitOfCompetency.id}`);
    }
  }, [item, navigate]);

  if (!item.unitOfCompetency) {
    return null;
  }

  const isSuperseded = item.status === CourseItemStatusSchema.enum.SUPERSEDED;
  const isUnscheduled =
    !isSuperseded &&
    item.unit_of_competency_id !== null &&
    !scheduledUnitIds.has(item.unit_of_competency_id);

  return (
    <>
      <ListItem
        key={item.id}
        value={item}
        position="relative"
        dragListener={false}
        dragControls={dragControls}
        onDragEnd={saveReorder}
      >
        <HStack gap={1} w="full">
          <HStack
            p={2}
            borderWidth="1px"
            borderColor={isSuperseded ? 'warning' : isUnscheduled ? 'success' : 'border.emphasis'}
            borderRadius="md"
            bg={
              isSuperseded ? 'bg.warning-subtle' : isUnscheduled ? 'bg.success-subtle' : 'bg.canvas'
            }
            gap={4}
            w="full"
            justify="start"
          >
            <HStack
              gap={4}
              align="center"
              w="full"
              flex="1"
              onClick={handleEditUnit}
              cursor="pointer"
              opacity={isSuperseded ? 0.75 : 1}
            >
              <Icon as={Layers} color="fg.emphasis" boxSize="32px" strokeWidth="1px" />
              <Stack gap={0} w="full">
                <HStack>
                  <Text
                    fontSize="lg"
                    lineHeight="7"
                    fontWeight="medium"
                    color="fg.default"
                    textDecoration={isSuperseded ? 'line-through' : undefined}
                  >
                    {item.unitOfCompetency.title}
                  </Text>
                  {isSuperseded ? (
                    <Tag size="sm" colorScheme="orange">
                      <Trans>Superseded</Trans>
                    </Tag>
                  ) : isUnscheduled ? (
                    <Tag size="sm" colorScheme="green">
                      <Trans>Not in schedule</Trans>
                    </Tag>
                  ) : null}
                </HStack>
                <HStack align="center">
                  <Text fontSize="md" lineHeight="6" color="fg.muted">
                    {item.unitOfCompetency.code}
                  </Text>
                  {coverage !== undefined ? (
                    <UnitCoverageBadge resources={coverage} languages={coverageLanguages} />
                  ) : null}
                  {item?.unitOfCompetency.unitContextCategory?.categoryName != null ? (
                    <Tag size="sm" variant="solid" colorScheme="blackAlpha">
                      {item?.unitOfCompetency.unitContextCategory.categoryName}
                    </Tag>
                  ) : null}
                  {item.unitOfCompetency.work_placement === true ? (
                    <Text fontSize="md" lineHeight="6" color="fg.muted">
                      <span aria-hidden>• </span>
                      <Trans>Work placement required</Trans>
                    </Text>
                  ) : null}
                </HStack>
                {isSuperseded && item.supersededByUnit ? (
                  <Text fontSize="sm" color="warning.emphasis">
                    {t`Superseded by`} {item.supersededByUnit.code} — {item.supersededByUnit.title}
                  </Text>
                ) : null}
              </Stack>
            </HStack>
            <Menu gutter={12}>
              <MenuButton
                as={IconButton}
                aria-label={t`More options`}
                _hover={{ color: 'brand.600' }}
                _active={{ color: 'brand.600', bg: 'bg.accent.canvas' }}
                icon={<Icon as={MoreHorizontalIcon} />}
                size="sm"
                variant="tertiary"
                onClick={() => {
                  setSelectedCourseItemId(item.id);
                }}
              />
              <MenuList fontSize="md">
                <MenuItem icon={<PencilIcon size={iconSize.md} />} onClick={handleEditUnit}>
                  <Trans>Edit</Trans>
                </MenuItem>

                {!isSuperseded ? (
                  <MenuItem icon={<Replace size={iconSize.md} />} onClick={onSupersededModalOpen}>
                    <Trans>Mark as superseded</Trans>
                  </MenuItem>
                ) : null}

                <Tooltip
                  isDisabled={!isSuperseded}
                  label={t`Superseded units cannot be deleted`}
                  placement="left"
                >
                  <MenuItem
                    icon={
                      isSuperseded ? (
                        <BanIcon size={iconSize.md} />
                      ) : (
                        <Trash2Icon size={iconSize.md} />
                      )
                    }
                    isDisabled={isSuperseded}
                    onClick={() => setIsDeleteConfirmationAlertOpen(true)}
                  >
                    <Trans>Delete</Trans>
                  </MenuItem>
                </Tooltip>
              </MenuList>
            </Menu>
          </HStack>
          <IconButton
            _hover={{ color: 'brand.600' }}
            icon={<Icon as={GripVerticalIcon} />}
            cursor={'grab'}
            size="sm"
            variant="tertiary"
            aria-label={t`Reorder course item`}
            onPointerDown={(e) => dragControls.start(e)}
          />
        </HStack>
      </ListItem>

      {isSupersededModalOpen ? (
        <CourseItemMarkSupersededModal
          isOpen={isSupersededModalOpen}
          onClose={onSupersededModalClose}
          courseItem={item}
          courseId={courseId}
          allCourseItems={allCourseItems}
        />
      ) : null}
    </>
  );
}
